import { x25519 } from '@noble/curves/ed25519.js';

export interface RawKeyPair {
  privateKey: ArrayBuffer;
  publicKey: ArrayBuffer;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return Uint8Array.from(bytes).buffer as ArrayBuffer;
}

function toUint8Array(buffer: ArrayBuffer): Uint8Array {
  return new Uint8Array(buffer);
}

function assertKeyLength(bytes: Uint8Array, label: string): void {
  if (bytes.byteLength !== 32) {
    throw new Error(`${label} must be 32 bytes`);
  }
}

export async function generateX25519KeyPair(): Promise<RawKeyPair> {
  const privateKey = x25519.utils.randomSecretKey();
  const publicKey = x25519.getPublicKey(privateKey);
  return {
    privateKey: toArrayBuffer(privateKey),
    publicKey: toArrayBuffer(publicKey)
  };
}

export async function deriveX25519SharedSecret(
  privateKeyData: ArrayBuffer,
  publicKeyData: ArrayBuffer
): Promise<ArrayBuffer> {
  const privateKey = toUint8Array(privateKeyData);
  const publicKey = toUint8Array(publicKeyData);
  assertKeyLength(privateKey, 'X25519 private key');
  assertKeyLength(publicKey, 'X25519 public key');
  return toArrayBuffer(x25519.getSharedSecret(privateKey, publicKey));
}

export async function exportPublicKey(keyData: ArrayBuffer): Promise<ArrayBuffer> {
  return keyData.slice(0);
}

export async function importPublicKey(keyData: ArrayBuffer): Promise<ArrayBuffer> {
  const bytes = toUint8Array(keyData);
  assertKeyLength(bytes, 'X25519 public key');
  return keyData.slice(0);
}

export async function importPrivateKey(keyData: ArrayBuffer): Promise<ArrayBuffer> {
  const bytes = toUint8Array(keyData);
  assertKeyLength(bytes, 'X25519 private key');
  return keyData.slice(0);
}
